import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Youtube, Instagram, Facebook, Linkedin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0A1538] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-4 inline-flex">
              <img
                src="https://customer-assets.emergentagent.com/job_9f281881-bcee-4052-97e6-4b1f664efaf6/artifacts/a8dr4my2_Screen%20Shot%202024-08-28%20at%2010.09.31%20AM.png"
                alt="Pecaro Consulting"
                className="h-12 w-auto"
              />
              <span className="font-bold text-xl">PECARO CONSULTING</span>
            </Link>
            <p className="text-gray-300 mb-4 max-w-md">
              AI Automation • Customer Experience • Digital Transformation
            </p>
            <p className="text-gray-400 text-sm">
              Helping small businesses communicate better, work smarter, and grow faster.
            </p>
            
            {/* Social Media Links */}
            <div className="mt-6">
              <h4 className="font-semibold text-sm mb-3">Connect with us on Social Media:</h4>
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/company/pecaro-consulting-inc/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#227AFF] flex items-center justify-center transition-all"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61556375871858"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#227AFF] flex items-center justify-center transition-all"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/pecaroconsultinginc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#227AFF] flex items-center justify-center transition-all"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.youtube.com/@PecaroConsultingInc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#227AFF] flex items-center justify-center transition-all"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a
                  href="https://www.tiktok.com/@pecaro_consulting_inc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#227AFF] flex items-center justify-center transition-all"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/smartstart" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  SmartStart™
                </Link>
              </li>
              <li>
                <Link to="/altam" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  Altam™
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <Mail className="h-5 w-5 mt-1 text-[#227AFF]" />
                <a href="mailto:info@pecaroconsulting.com" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  info@pecaroconsulting.com
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <Phone className="h-5 w-5 mt-1 text-[#227AFF]" />
                <a href="tel:1-877-331-8445" className="text-gray-300 hover:text-[#227AFF] transition-colors">
                  1-877-331-8445
                </a>
              </li>
              <li className="flex items-start space-x-2">
                <MapPin className="h-5 w-5 mt-1 text-[#227AFF]" />
                <span className="text-gray-300">Serving businesses nationwide</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Pecaro Consulting Inc. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;